import { useState, useRef, useEffect } from 'react'
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../App'
import { FiHome, FiDatabase, FiCode, FiUsers, FiLayout, FiLogOut, FiBarChart2, FiBriefcase, FiChevronDown, FiSearch, FiCheck, FiGitMerge, FiMenu, FiChevronLeft, FiActivity, FiX, FiCpu, FiTrendingUp, FiServer, FiAlertCircle, FiZap, FiList } from 'react-icons/fi'
import useIsMobile from '../../utils/useIsMobile'

const adminLinks = [
  { to: '/', icon: FiHome, label: 'Dashboardlar' },
  { to: '/datasources', icon: FiDatabase, label: 'Veri Kaynaklari' },
  { to: '/relations', icon: FiGitMerge, label: 'Iliskiler' },
  { to: '/queries', icon: FiCode, label: 'Sorgular' },
  { to: '/templates', icon: FiLayout, label: 'Sablonlar' },
  { to: '/users', icon: FiUsers, label: 'Kullanicilar' },
]

const viewerLinks = [
  { to: '/', icon: FiHome, label: 'Dashboardlar' },
]

export default function Layout() {
  const { user, logout, selectedCompanyId, selectCompany, companies } = useAuth()
  const navigate = useNavigate()
  const selectedCompanyObj = companies.find(c => c.id === selectedCompanyId)
  // Spesifik firma secildiyse onun feature'lari; secilmediyse (Tum Firmalar) erisilebilen tum firmalarin birlesimi
  const allCompanyFeatures = companies.reduce((acc, c) => ({ ...acc, ...(c.features || {}) }), {})
  const features = selectedCompanyObj?.features || allCompanyFeatures
  const baseLinks = user?.is_admin ? adminLinks : viewerLinks
  let links = baseLinks
  if (features.albero_ozet) links = [...links, { to: '/albero-ozet', icon: FiActivity, label: 'Anlık Rapor' }]
  if (features.plasiyer_raporu) links = [...links, { to: '/plasiyer', icon: FiUsers, label: 'Plasiyer Raporu' }]
  if (features.gonderilmeyen_fatura) links = [...links, { to: '/gonderilmeyen-faturalar', icon: FiAlertCircle, label: 'Gönderilmeyen Faturalar' }]
  if (features.stok_raporu) links = [...links, { to: '/stok', icon: FiDatabase, label: 'Stok' }]
  // Sistem sayfa izinleri: 72=Anlık Rapor, 73=AI Analiz, 74=AI Tahmin
  const ACCESS = user?.accessible_page_ids || []
  const isAdmin = user?.is_admin
  if (isAdmin || ACCESS.includes(72)) links = [...links, { to: '/anlik-rapor', icon: FiActivity, label: 'Anlık Rapor' }]
  // Akilli Arama: verisi aranabilir firmalar (Pocan 5, Satici Gross 7, Aksantas 8) + yonetici
  if (isAdmin || [5, 7, 8].includes(user?.company_id)) links = [...links, { to: '/akilli-arama', icon: FiZap, label: 'Akıllı Arama' }]
  if (isAdmin || ACCESS.includes(73)) links = [...links, { to: '/ai-analiz', icon: FiCpu, label: 'Yapay Zeka Analiz' }]
  if (isAdmin || ACCESS.includes(74)) links = [...links, { to: '/ai-tahmin', icon: FiTrendingUp, label: 'Yapay Zeka Tahmin' }]
  if (isAdmin || ACCESS.includes(72)) links = [...links, { to: '/cari-rapor', icon: FiSearch, label: 'Cari Rapor' }]
  if (user?.is_super_admin) links = [...links, { to: '/arama-kayitlari', icon: FiList, label: 'Arama Kayıtları' }]
  if (isAdmin) links = [...links, { to: '/sorgu-gecmisi', icon: FiServer, label: 'Sorgu Geçmişi' }]

  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [companyDropdownOpen, setCompanyDropdownOpen] = useState(false)
  const [companySearch, setCompanySearch] = useState('')
  const dropdownRef = useRef(null)
  const location = useLocation()

  // Mobile: rota degisince drawer'i kapat
  useEffect(() => { setMobileSidebarOpen(false) }, [location.pathname])

  const selectedCompany = companies.find(c => c.id === selectedCompanyId)

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCompanyDropdownOpen(false)
        setCompanySearch('')
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  )

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  // Dark mode'da logoyu beyaza cevir
  const logoFilter = { filter: 'brightness(0) invert(1)' }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile top bar — sadece mobilde gozukur */}
      {isMobile && (
        <div className="md:hidden fixed top-0 left-0 right-0 z-30 bg-white dark:bg-sidebar border-b border-gray-200 dark:border-gray-700/50 px-3 py-2 flex items-center justify-between shadow-sm">
          <button onClick={() => setMobileSidebarOpen(true)} className="p-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 rounded">
            <FiMenu size={18} />
          </button>
          <img src="/logo/qr-analiz.png" alt="QR Analiz" className="h-7 dark:[filter:brightness(0)_invert(1)]" onError={(e) => { e.target.style.display='none' }} />
          <button onClick={handleLogout} className="p-1.5 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 rounded" title="Cikis">
            <FiLogOut size={16} />
          </button>
        </div>
      )}
      {/* Mobile drawer overlay backdrop */}
      {isMobile && mobileSidebarOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setMobileSidebarOpen(false)} />
      )}
      {/* Sidebar - Acik modda beyaz, koyu modda lacivert */}
      <aside className={`bg-white dark:bg-sidebar border-r border-gray-200 dark:border-gray-700/50 text-gray-700 dark:text-white flex flex-col flex-shrink-0 transition-all duration-200 ${
        isMobile
          ? `fixed inset-y-0 left-0 z-50 w-64 transform ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
          : (sidebarOpen ? 'w-56' : 'w-14')
      }`}>
        {/* Logo + Toggle */}
        {(isMobile || sidebarOpen) ? (
          <div className="px-2 pt-2 pb-2 flex items-center gap-1">
            <div className="flex-1 flex items-center justify-center overflow-hidden" style={{ height: '36px' }}>
              <img src="/logo/qr-analiz.png" alt="QR Analiz"
                className="dark:[filter:brightness(0)_invert(1)]"
                style={{ width: '100%', height: '52px', objectFit: 'cover', objectPosition: 'center' }}
                onError={(e) => { e.target.style.display='none' }} />
            </div>
            {isMobile ? (
              <button onClick={() => setMobileSidebarOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded flex-shrink-0">
                <FiX size={16} />
              </button>
            ) : (
              <button onClick={() => setSidebarOpen(false)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded flex-shrink-0">
                <FiChevronLeft size={14} />
              </button>
            )}
          </div>
        ) : (
          <div className="px-1 pt-2 pb-2 flex flex-col items-center gap-1">
            <div className="w-full flex items-center justify-center">
              <img src="/logo/qryazilim-alt.png" alt="QR"
                className="h-8 w-8 object-contain dark:[filter:brightness(0)_invert(1)]"
                onError={(e) => { e.target.style.display='none' }} />
            </div>
            <button onClick={() => setSidebarOpen(true)} className="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded">
              <FiMenu size={14} />
            </button>
          </div>
        )}

        {/* Firma Secici */}
        {(isMobile || sidebarOpen) && user?.is_super_admin && (
          <div className="px-2 pb-2" ref={dropdownRef}>
            <button
              onClick={() => setCompanyDropdownOpen(!companyDropdownOpen)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg text-xs transition-colors border border-gray-300 dark:border-white/10"
            >
              <FiBriefcase size={12} className="text-primary-500 dark:text-primary-400 flex-shrink-0" />
              <span className="flex-1 text-left truncate text-gray-700 dark:text-gray-200">
                {selectedCompany ? selectedCompany.name : 'Tum Firmalar'}
              </span>
              <FiChevronDown size={12} className={`text-gray-400 transition-transform ${companyDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {companyDropdownOpen && (
              <div className="absolute left-2 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border dark:border-gray-700 z-50 max-h-72 flex flex-col" style={{ width: '200px' }}>
                <div className="p-1.5 border-b dark:border-gray-700">
                  <div className="relative">
                    <FiSearch size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input value={companySearch} onChange={(e) => setCompanySearch(e.target.value)}
                      placeholder="Firma ara..." className="w-full pl-6 pr-2 py-1 text-[11px] border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-100 outline-none" autoFocus />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-52">
                  <button onClick={() => { selectCompany(null); setCompanyDropdownOpen(false); setCompanySearch('') }}
                    className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] hover:bg-primary-50 dark:hover:bg-gray-700 ${selectedCompanyId === null ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-300'}`}>
                    <FiBriefcase size={10} className="text-gray-400" />
                    <span className="flex-1 text-left">Tum Firmalar</span>
                    {selectedCompanyId === null && <FiCheck size={10} className="text-primary-600" />}
                  </button>
                  {filteredCompanies.map(c => (
                    <button key={c.id} onClick={() => { selectCompany(c.id); setCompanyDropdownOpen(false); setCompanySearch('') }}
                      className={`w-full flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] hover:bg-primary-50 dark:hover:bg-gray-700 ${selectedCompanyId === c.id ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300' : 'text-gray-600 dark:text-gray-300'}`}>
                      <FiBriefcase size={10} className="text-gray-400" />
                      <span className="flex-1 text-left truncate">{c.name}</span>
                      {selectedCompanyId === c.id && <FiCheck size={10} className="text-primary-600" />}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border-b border-gray-200 dark:border-white/10" />

        {/* Nav */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 text-xs transition-colors ${
                  isActive ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                } ${(!isMobile && !sidebarOpen) ? 'justify-center' : ''}`
              }
              title={(!isMobile && !sidebarOpen) ? link.label : undefined}
            >
              <link.icon size={15} />
              {(isMobile || sidebarOpen) && link.label}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className={`p-3 border-t border-gray-200 dark:border-white/10 ${(!isMobile && !sidebarOpen) ? 'text-center' : ''}`}>
          {(isMobile || sidebarOpen) && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1.5 truncate">
              {user?.full_name || user?.username}
              {user?.is_super_admin && <span className="ml-1 text-primary-500 dark:text-primary-400">(SA)</span>}
            </div>
          )}
          <button onClick={handleLogout} className={`flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white ${(!isMobile && !sidebarOpen) ? 'mx-auto' : ''}`}
            title="Cikis Yap">
            <FiLogOut size={13} />
            {(isMobile || sidebarOpen) && 'Cikis Yap'}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className={`flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 dark:text-gray-100 ${isMobile ? 'pt-12' : ''}`}>
        <Outlet />
      </main>
    </div>
  )
}
